<template>
  <v-container fluid>
    <v-card class="d-flex justify-space-between backgroundWhite mb-3" flat>
      <v-card flat class="backgroundWhite">
        <v-skeleton-loader v-if="!current_period" type="chip" />
        <v-chip v-else class="text-caption" color="backgroundLight" text-color="primaryOutline" label>
          <v-icon small class="mr-2" color="primaryOutline">mdi-clock-outline</v-icon>
          <span>{{ current_period }}</span>
          <!--TODO: When period skipped, text field is broken, maybe add another flag?-->
        </v-chip>
      </v-card>

      <v-spacer></v-spacer>

      <v-card flat class="mr-3 backgroundWhite">
        <v-menu :close-on-content-click="false" :nudge-width="300" max-width="300" offset-y>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn text outlined color="vtblue" v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                  <v-icon>mdi-minus-box-multiple</v-icon>
                </v-btn>
              </template>
              <span>Toggle Visible Charts</span>
            </v-tooltip>
          </template>
          <v-card class="px-6 pt-3 py-0">
            <div class="d-flex justify-space-between">
              <p class="text-center text-h6">Visible Charts</p>
            </div>
            <v-divider class="mt-0" />
            <v-row class="ma-0">
              <v-treeview
                dense
                selectable
                hoverable
                item-disabled="locked"
                :disabled="is_loading"
                :value="visible_charts"
                :items="page_content.trends_data_treeview_fields"
                @input="updateVisibleCharts"
              />
            </v-row>
          </v-card>
        </v-menu>
      </v-card>
      <v-card flat class="d-flex mr-3 backgroundWhite">
        <v-btn-toggle dense multiple tile>
          <v-btn
            v-for="phase in general_config.chart_phase_options"
            :key="phase.id"
            text
            :color="phase.color"
            @click="togglePhase(phase.id, phase.label)"
          >
            <v-icon left :color="show_phases[phase.id] ? phase.color : 'titleLight'">
              {{ show_phases[phase.id] ? "mdi-eye" : "mdi-eye-off" }}
            </v-icon>
            {{ phase.label }}
          </v-btn>
        </v-btn-toggle>
      </v-card>
      <v-card flat class="mr-3 backgroundWhite">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn @click="showSettingsMenu($event)" text outlined color="vtred" v-bind="attrs" v-on="on">
              <v-icon> mdi-settings </v-icon>
            </v-btn>
          </template>
          <span>Chart Settings</span>
        </v-tooltip>
      </v-card>

      <v-spacer></v-spacer>

      <v-card class="backgroundWhite" flat>
        <DatePickerVT @updatePeriod="updatePeriod" :is_loading="is_loading" />
      </v-card>
    </v-card>
    <div v-if="chart_data && !is_loading">
      <LightningChartVT
        :chart_data="chart_data"
        :config="page_content.scavenger_charts"
        :charts="charts"
        :visible_charts="visible_charts"
        :types="types"
        :new_type_data="new_type_data"
        :is_loading="is_loading"
        :settings_menu_props="settings_menu_props"
        :new_chart_data="new_chart_data"
        :toggle_phase="toggle_phase"
        :visible_types="visible_types"
        @getTypeData="getTypeData"
        @setVisibleTypes="setVisibleTypes"
        @resetChartOptions="resetChartOptions"
        :under_development="true"
      />
    </div>
    <div v-else>
      <v-skeleton-loader
        v-for="(chart, index) in page_content.chart_config_lightning"
        :key="`loader-${index}`"
        type="image"
        class="py-3"
      />
    </div>
  </v-container>
</template>

<script>
import { axios_get } from "../services/vt_service";
import { scv } from "../services/powerdata";
import { content } from "../../config/scavenger.js";
import { content as general_content } from "../../config/general.js";

import moment from "moment";

import LightningChartVT from "../components/LightningChartVT.vue";
import DatePickerVT from "../components/DatePickerVT.vue";

export default {
  name: "ScavengerAssetTrendsData",
  props: {
    device_id: String,
    active_feeders: String,
    start_date: String,
    end_date: String,
  },
  components: {
    LightningChartVT,
    DatePickerVT,
  },
  data: function () {
    return {
      page_content: null,
      charts: [
        "RMSVoltage",
        "Frequency",
        "F1_Current",
        "F1_ActivePower",
        "F1_ReactivePower",
        "F1_ApparentPower",
        "F2_Current",
        "F2_ActivePower",
        "F2_ReactivePower",
        "F2_ApparentPower",
        "F3_Current",
        "F3_ActivePower",
        "F3_ReactivePower",
        "F3_ApparentPower",
        "F4_Current",
        "F4_ActivePower",
        "F4_ReactivePower",
        "F4_ApparentPower",
        "F5_Current",
        "F5_ActivePower",
        "F5_ReactivePower",
        "F5_ApparentPower",
        "F6_Current",
        "F6_ActivePower",
        "F6_ReactivePower",
        "F6_ApparentPower",
      ],
      visible_charts: [
        "RMSVoltage",
        "Frequency",
        "F1_Current",
        "F1_ActivePower",
        "F1_ReactivePower",
        "F1_ApparentPower",
      ],
      charts_with_data: [],
      types: ["Mean"],
      visible_types: ["Mean"],
      chart_data: null,
      is_loading: false,
      general_config: null,
      show_phases: [true, true, true], // L1 L2 L3
      toggle_phase: null,
      settings_menu_props: null,
      is_live_stream_on: true,
      current_period_values: null,
      new_chart_data: null,
      new_type_data: null,
    };
  },
  created() {
    this.page_content = content;
    this.general_config = general_content;
  },
  async mounted() {
    const scavenger_chart_data = await this.getScavengerTrendsChartData();
    this.chart_data = this.formatTrendsDataChart(scavenger_chart_data);
  },
  methods: {
    async getScavengerTrendsChartData(parameters) {
      this.is_loading = this.is_live_stream_on ? false : true;

      const params = {
        start_date: null,
        end_date: null,
        aggregation: null,
        charts: this.visible_charts,
        types: this.types,
      };

      if (parameters) Object.assign(params, parameters);
      const response = await axios_get(scv.get_scavenger_trends_data_chart, { id: this.device_id }, params);
      this.is_loading = false;
      return response;
    },
    formatTrendsDataChart(data, charts, type) {
      // In the initial call, the types will be pre-defined, so we can directly format the data
      // However, later we can get new type data from the API and format it accordingly
      // So, no need to format the existing data again

      const current_charts = charts ? charts : this.charts;
      const types = type ? [type] : this.types;
      const formatted_data = {};

      current_charts.forEach((chart) => {
        const chart_key = chart.split("_").pop();
        const chart_config = this.page_content.scavenger_charts[chart_key];
        types.forEach((type) => {
          chart_config.values.forEach((value) => {
            const value_id = value ? `${value}_${chart}_${type}` : `${chart}_${type}`;
            const chart_data = this.chartDataFormatHelper(data, value_id, chart, type, value, chart_config, chart_key);
            if (!formatted_data[chart]) formatted_data[chart] = [];
            formatted_data[chart].push(chart_data);
            const isVisible = this.getChartVisibility(chart_config, chart_data);
            if (isVisible && !this.visible_charts.includes(chart)) this.visible_charts.push(chart);
            if ((chart_data.values.yValues.length > 0 || charts) && !this.charts_with_data.includes(chart)) {
              this.charts_with_data.push(chart);
            }
          });
        });
      });
      return formatted_data;
    },
    chartDataFormatHelper(data, value_id, chart, type, value, chart_config, chart_key, f) {
      const feeder = this.getFeederNumber([chart]);
      const chart_data = {
        id: value_id,
        config_id: chart_key,
        type: type,
        value: value,
        label: value ? `${value} ${chart_config.label} ${type}` : `${chart_config.label} ${type}`,
        feeder: feeder ? feeder : null,
        values: this.getChartXYValues(data, value_id, chart_config),
      };
      return chart_data;
    },
    getChartXYValues(data, value_id, chart_config) {
      // We return empty array if the data is not available
      const xValues = data.map((e) => moment(e.Timestamp).valueOf());
      const yValues = data.map((e) => this.getDataPointValue(e, value_id, chart_config)).filter((e) => e !== null);
      return {
        xValues: yValues.length > 0 ? xValues : [],
        yValues: yValues,
      };
    },
    getChartVisibility(chart_config, chart_data) {
      // Get the visibility of the chart based on the type and value and feeder
      if (!chart_config.show) return false;
      if (chart_data.values.yValues.length == 0) return false;
      return true;
    },
    getDataPointValue(value, value_id, config) {
      if (config && config.suffix && config.suffix == "F") return this.$options.filters.toFahrenheit(value[value_id]);
      return value[value_id] ? parseFloat(value[value_id]) : null;
    },
    async updatePeriod(e) {
      //this.visible_charts = [];
      this.charts_with_data = [];

      const parameters = {
        start_date: e.start_date,
        end_date: e.end_date,
        aggregation: e.aggregation,
      };
      const response = await this.getScavengerTrendsChartData(parameters);
      this.chart_data = this.formatTrendsDataChart(response);
      this.is_live_stream_on = e.liveStream;
      this.current_period_values = e;
    },
    async getTypeData(type) {
      this.is_loading = true;
      // We need to call getScavengerChartData API with only with the received type
      const parameters = {
        start_date: null,
        end_date: null,
        types: [type],
      };

      const response = await this.getScavengerTrendsChartData(parameters);
      this.new_type_data = this.formatTrendsDataChart(response, null, type);
      this.types.push(type);
      this.is_loading = false;
    },
    setVisibleTypes(event) {
      if (event.show) {
        this.visible_types.push(event.type);
      } else {
        this.visible_types = this.visible_types.filter((type) => type !== event.type);
      }
    },
    togglePhase(id, label) {
      this.show_phases[id] = !this.show_phases[id];
      this.toggle_phase = { value: label, show: this.show_phases[id] };
    },
    showSettingsMenu(e) {
      this.settings_menu_props = { state: true, x: e.clientX, y: e.clientY };
    },
    async updateVisibleCharts(event) {
      this.visible_charts = event;
      const charts_without_data = event.filter((e) => !this.charts_with_data.includes(e));
      let data = [];
      if (charts_without_data.length > 0) {
        const parameters = {
          charts: charts_without_data,
        };
        if (!this.is_live_stream_on) {
          parameters.start_date = this.current_period_values.start_date;
          parameters.end_date = this.current_period_values.end_date;
          parameters.aggregation = this.current_period_values.aggregation;
        }
        const response = await this.getScavengerTrendsChartData(parameters);
        data = this.formatTrendsDataChart(response, charts_without_data, null);
      }
      this.new_chart_data = event.reduce((acc, c) => {
        acc[c] = {
          data: data[c] || null,
          show: true,
        };
        return acc;
      }, {});
    },
    getFeederNumber(charts) {
      const chart = charts.find((chart) => {
        const values = chart.split("_");
        return Object.values(this.page_content.feeder_keys).includes(values[0]);
      });

      return chart ? chart.split("_")[0] : null;
    },
    getChartID(charts) {
      // This is a helper function that returns the actual chart names and other details from the label
      // Such as; Values with no value: Frequency
      // Received charts: ['Current_F2', 'Active_Power_F2', 'Reactive_Power_F2', 'Apparent_Power_F2']
      // Returned charts: { feeder: 'f2', charts: ['Current', 'Active_Power', 'Reactive_Power', 'Apparent_Power'] }
      if (!charts || charts.length == 0) return null;
      let feeder = "";
      const filteredCharts = [];

      charts.forEach((item) => {
        const match = item.match(/_F(\d+)$/);

        if (match) {
          // Extract and set feeder number (assuming all _F values are the same)
          if (!feeder) feeder = `f${parseInt(match[1]) - 1}`.toLowerCase();

          // Remove feeder suffix and check if it exists in this.page_content.scavenger_charts
          const chartName = item.replace(/_F\d+$/, "");
          if (this.page_content.scavenger_charts[chartName]) filteredCharts.push(chartName);
        } else {
          // Add charts that don’t have a feeder suffix directly
          if (this.page_content.scavenger_charts[item]) filteredCharts.push(item);
        }
      });

      return { feeder, charts: filteredCharts };
    },
    resetChartOptions() {
      this.visible_types = ["Mean"];
      this.types = ["Mean"];
    },
  },
  computed: {
    current_period() {
      if (this.is_live_stream_on) {
        return `Last 24 Hrs: ${new Date(parseInt(this.start_date)).toLocaleString("en-US", {
          hour12: false,
        })}`;
      }
      return `${this.current_period_values.text}: ${moment(this.current_period_values.end_date).format("D MMM")} - ${moment(
        this.current_period_values.start_date
      ).format("D MMM YYYY")}`;
    },
    enabled_feeders() {
      return this.page_content.trends_data_treeview_fields
        .filter((elem) => elem.index != null) // Eliminating fields that has no feeder index
        .map((e) => (e.locked = this.active_feeders[e.index] === "0" ? true : false)); // Locking the feeders that are not active
    },
  },
};
</script>
